import { Injectable } from '@angular/core';
import type { Json } from '@se/cp.common';
import { RouterDataService } from '@se/se.ui.html5';
import { uniq } from 'lodash-es';
import { forkJoin, Observable } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';

import { ICustomPageLayoutData, ICustomPageMasterData, ICustomPageMasterDataResolver } from './master-data';
import { CpRouterData } from '@se/models';
import { filterConstraintsByDisabler } from './utils';
import { CpHttpService } from './cp-http.service';
import { EstensaWaitingService } from '../../services/waiting.service';

@Injectable({
    providedIn: 'root'
})
export class HttpMasterDataResolver implements ICustomPageMasterDataResolver {
    private idCustomPageRoot = '';
    private idExaminations: string[] = [];
    private idModelTypes: string[] = [];

    constructor(
        private cpHttpService: CpHttpService,
        private routerData: RouterDataService,
        private waitingService: EstensaWaitingService,
    ) {
    }

    getCustomPageRoot() {
        return this.idCustomPageRoot;
    }

    getModelTypes() {
        return this.idModelTypes;
    }

    getData() {
        return this.cpHttpService.getData(this.idExaminations, this.idCustomPageRoot) as any as Observable<Json>;
    }

    getLayout(idCustomPage: string): Observable<ICustomPageLayoutData> {
        return forkJoin([
            this.cpHttpService.getDataset(idCustomPage),
            this.cpHttpService.getLayout(idCustomPage),
            this.cpHttpService.getItemSourcesWithItemsByIdCustomPage(idCustomPage),
            this.cpHttpService.getPlaceholdersByIdCustomPage(idCustomPage),
        ]).pipe(
            map(([dataset, layout, dataSources, placeholders]) => ({
                constraints: filterConstraintsByDisabler(layout?.jsonConstraint?.array ?? []),
                dataset,
                dataSources: dataSources?.jsonItemsSource ?? {},
                idModelTypes: this.idModelTypes,
                layout: layout?.jsonPanel,
                placeholders: placeholders?.jsonDefaultValue,
            })),
        );
    }

    getMasterData(): Observable<ICustomPageMasterData> {
        const routerData = this.routerData.data as CpRouterData;
        this.idExaminations = routerData.examinations.map((e) => e.id);
        this.idCustomPageRoot = routerData.idCustomPageRoot;

        this.waitingService.isWaiting = true;

        // Recupera i dati.
        return this.cpHttpService.getData(this.idExaminations, this.idCustomPageRoot).pipe(
            switchMap((model: any) => {
                // Recupera il layout e gli altri dati accessori.
                this.idModelTypes = uniq(model.IdModelTypeList);

                return this.getLayout(model.IdCustomPage).pipe(
                    map((layoutData) => ({
                        ...layoutData,
                        idCustomPage: model.IdCustomPage,
                        idModelTypes: this.idModelTypes,
                        model: { Report: model },
                    })),
                );
            }),
            finalize(() => (this.waitingService.isWaiting = false)),
        );
    }
}
